import type { GameId } from "./index.js";
import type { GuestSessionResponse } from "./identity.js";

/** Phone login (SMS code), cloud saves and contact consent. docs/design/phone-login-admin.md */

/** The consent text shown beside the WhatsApp box; bump when its wording changes. */
export const WHATSAPP_CONSENT_VERSION = "wa-2026-10-27" as const;
/** Seconds before "Yeni Kod Gönder" may be pressed again (the code itself lives 2 minutes). */
export const LOGIN_CODE_RESEND_SECONDS = 120;
export const LOGIN_CODE_LENGTH = 4;

export interface PhoneStartRequest {
  /** Any common Turkish writing: 0530…, 530…, +90 530… */
  phone: string;
  lang?: "tr" | "en";
}

export interface PhoneStartResponse {
  sent: true;
  seconds: number;
  /** `+90 5•• ••• •• 21` */
  phoneHint: string;
}

export interface PhoneVerifyRequest {
  phone: string;
  code: string;
  /** H9: the player confirmed being 13 or older. */
  ageConfirmed: true;
  /** The unticked-by-default WhatsApp box. */
  whatsappOptIn: boolean;
}

export interface PhoneVerifyResponse {
  /** The session to use from now on (a new one when the phone belonged to another device's account). */
  session: GuestSessionResponse;
  /** True when this device moved to the account the phone already had: merge local progress into it. */
  switchedAccount: boolean;
}

export interface ContactConsentRequest {
  channel: "whatsapp";
  granted: boolean;
}

/** A game's local keys (`arcade.<id>`, `arcade.<id>.map`, …) as JSON values. */
export type SaveData = Record<string, unknown>;

export interface SaveRecord {
  gameId: GameId;
  data: SaveData;
  version: number;
  updatedAt: string;
}

export interface SavesResponse {
  saves: SaveRecord[];
}

export interface PutSaveRequest {
  data: SaveData;
  /** The version this save was merged from; 0 for a first save. A stale one answers 409 with the current save. */
  baseVersion: number;
}

export interface PutSaveConflict {
  error: { code: "REVISION_CONFLICT"; message: string; requestId: string };
  current: SaveRecord;
}

/**
 * `GET /v1/features`: what this API offers, asked without a session. A guest's progress is never sent to the
 * server; saves sync only for phone accounts (H10: minimum data). `MEMBER_HINT_COOKIE` (`ej_member=1`, readable by
 * the page, no identifier in it) tells a browser's other game subdomains that this browser has a phone account, so
 * they open a session and sync; without it or a stored installation, a page contacts nothing but this route.
 */
export interface FeaturesResponse {
  identity: boolean;
  saves: boolean;
  phoneLogin: boolean;
}

export const MEMBER_HINT_COOKIE = "ej_member" as const;
